/**
 * engine-requirements — decide which heavy WASM engine(s) a parsed intent needs.
 *
 * EarthVoice lazy-loads two big engines: DuckDB-WASM (~33 MB, in-browser spatial
 * SQL) and geolibre-wasm (~22 MB, the geoprocessing backend behind buffer_zone
 * and future geolibre capabilities). Neither downloads at page load: geolibre
 * prewarms on browser idle, and DuckDB (CL-140, BUG-050) prewarms on first
 * user interaction or idle. A query can therefore arrive before an engine is
 * ready; dispatching then renders blank. The cold-start gate in main.js uses
 * this map to decide whether to wait (with a visible "warming up" state) —
 * for DuckDB that wait is also what TRIGGERS the lazy init on demand.
 *
 * Pure + exported so it can be unit-tested without standing up the app.
 *
 * To opt a NEW capability into the gate, add its op name (the `op` it executes
 * as, or its legacy intent `type`) to the appropriate set below.
 */

// Ops/intent-types backed by the geolibre WASM runner.
// CL-133: clip_to_region / convex_hull / voronoi / centroid were shipped
// (CL-118..CL-121) without being added here, so they bypassed the cold-start
// gate and could dispatch against a still-warming WASM engine.
export const GEOLIBRE_OPS = new Set([
  'buffer_zone',
  'clip_to_region',
  'convex_hull',
  'voronoi',
  'centroid',
])

// Ops/intent-types that run in-browser DuckDB SQL. Covers pipeline op names,
// legacy intent `type` strings, AND `action` values (division_action intents /
// `boundaries` pipeline steps carry the DuckDB-backed work in `action`, e.g.
// territory_pois — CL-140 added action tokens when DuckDB went fully lazy).
export const DUCKDB_OPS = new Set([
  'duckdb_query',
  'proximity',
  'proximity_join',
  'point_in_region',
  'poi_in_admin',
  'h3_density',
  'division_choropleth',
  'territory_pois',
  'forced_poi',
  'centrography',
  'moran',
  'lisa',
  'gravity',
  'gravity_model',
  'typology',
  'change_map',
  'deviational_ellipse',
  'point_pattern',
])

/**
 * @param {object} intent — parsed intent (legacy `{type,...}` or `{pipeline:[...]}`)
 * @returns {Set<'duckdb'|'geolibre'>} engines this command will use
 */
export function requiredEngines(intent) {
  const engines = new Set()
  if (!intent || typeof intent !== 'object') return engines

  const steps = Array.isArray(intent.pipeline) ? intent.pipeline : []
  const opNames = steps.map((s) => s && s.op).filter(Boolean)
  // Actions too: `{type:'division_action', action:'territory_pois'}` and
  // `{op:'boundaries', action:'division_choropleth'}` both dispatch to
  // DuckDB-backed handlers even though type/op alone don't say so (CL-140).
  const actionNames = steps.map((s) => s && s.action).filter(Boolean)
  const tokens = [intent.type, intent.action, ...opNames, ...actionNames].filter(Boolean)

  for (const t of tokens) {
    if (GEOLIBRE_OPS.has(t)) engines.add('geolibre')
    if (DUCKDB_OPS.has(t)) engines.add('duckdb')
  }

  // SQL-bearing intents always need DuckDB.
  if (intent.sql) engines.add('duckdb')

  // A division_action carrying a poi_category is adapted to poi_in_admin
  // (see legacy-adapter.js), which counts POIs via DuckDB.
  if (intent.type === 'division_action' && intent.poi_category) engines.add('duckdb')

  // buffer_zone over the cities layer also loads its source points from DuckDB.
  if (engines.has('geolibre')) {
    const bufStep = steps.find((s) => s && s.op === 'buffer_zone')
    const bufTarget = intent.type === 'buffer_zone' ? intent.target : bufStep && bufStep.target
    if (bufTarget === 'cities') engines.add('duckdb')
  }

  return engines
}
